/**
 * Which client company a platform role is currently acting inside.
 *
 * Held the way the session is (`app/session.ts`): module state plus a listener set, not React
 * context — `app/api.ts` builds the API client at module scope and reads this from outside React
 * entirely, which a context could not serve.
 *
 * **Persisted, unlike the access token.** That difference is deliberate rather than an oversight.
 * The token is a credential and lives only in memory so an XSS cannot lift it; a company's id and
 * name are not a credential — the server honours `X-Organisation-Id` only for a principal that may
 * use it, and ignores it for everyone else, so a stale value can widen nobody's reach. What it can
 * do is put a platform admin back in ASGTRANS's own tenant after a reload while they believed they
 * were still editing a client company. Forgetting is the worse failure here, so this is remembered.
 */

export interface OrganisationContext {
  id: string
  name: string
}

const KEY = 'deca.organisation'

type Listener = () => void
const listeners = new Set<Listener>()

function read(): OrganisationContext | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OrganisationContext>
    return typeof parsed.id === 'string' && typeof parsed.name === 'string'
      ? { id: parsed.id, name: parsed.name }
      : null
  } catch {
    // A hand-edited, truncated or foreign value is "no context" — never a crash at boot, and never
    // a half-built object that would send `X-Organisation-Id: undefined`.
    return null
  }
}

let current: OrganisationContext | null = read()

export function getOrganisationContext(): OrganisationContext | null {
  return current
}

export function setOrganisationContext(next: OrganisationContext | null): void {
  current = next
  if (next) localStorage.setItem(KEY, JSON.stringify(next))
  else localStorage.removeItem(KEY)
  for (const listener of listeners) listener()
}

export function subscribeOrganisation(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
